import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import Lenis from "lenis";
import { Toaster, toast } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { PlayerProvider } from "@/context/PlayerContext";
import { CartProvider } from "@/context/CartContext";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { PlayerBar } from "@/components/PlayerBar";
import { Footer } from "@/components/Footer";
import Home from "@/pages/Home";
import Catalogue from "@/pages/Catalogue";
import TrackDetail from "@/pages/TrackDetail";
import AuthPage from "@/pages/Auth";
import Compte from "@/pages/Compte";
import Panier from "@/pages/Panier";
import Admin from "@/pages/Admin";
import Artistes from "@/pages/Artistes";
import Artiste from "@/pages/Artiste";
import Journal from "@/pages/Journal";
import JournalArticle from "@/pages/JournalArticle";
import DevenirArtiste from "@/pages/DevenirArtiste";
import CGV from "@/pages/CGV";
import Contact from "@/pages/Contact";
import StudioNotifications from "@/pages/StudioNotifications";

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const sessionId = new URLSearchParams(location.hash.slice(1)).get("session_id");
    if (!sessionId) {
      navigate("/connexion");
      return;
    }
    api
      .post("/auth/google/session", { session_id: sessionId })
      .then((r) => {
        setUser(r.data);
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/compte", { state: { user: r.data } });
      })
      .catch(() => {
        toast.error("La connexion Google a échoué");
        navigate("/connexion");
      });
  }, [location, navigate, setUser]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-ink" data-testid="auth-callback">
      <div className="h-10 w-10 animate-spin rounded-full border-2 border-white/10 border-t-amber-500" />
    </div>
  );
};

const ScrollManager = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

function AppRouter() {
  const location = useLocation();
  // Check URL fragment (not query params) for session_id — must run synchronously during render
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <ScrollManager />
      <Navbar />
      <div className="min-h-screen">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/catalogue" element={<Catalogue />} />
          <Route path="/morceau/:id" element={<TrackDetail />} />
          <Route path="/connexion" element={<AuthPage />} />
          <Route path="/compte" element={<Compte />} />
          <Route path="/panier" element={<Panier />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/artistes" element={<Artistes />} />
          <Route path="/artiste/:slug" element={<Artiste />} />
          <Route path="/journal" element={<Journal />} />
          <Route path="/journal/:id" element={<JournalArticle />} />
          <Route path="/devenir-artiste" element={<DevenirArtiste />} />
          <Route path="/cgv" element={<CGV />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/studio/notifications" element={<StudioNotifications />} />
        </Routes>
      </div>
      <Footer />
      <PlayerBar />
    </>
  );
}

export default function App() {
  useEffect(() => {
    const lenis = new Lenis({ smoothWheel: true, lerp: 0.09 });
    window.__lenis = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <PlayerProvider>
            <Toaster theme="dark" position="top-center" toastOptions={{ style: { background: "#141417", border: "1px solid rgba(255,255,255,0.1)", color: "#fff" } }} />
            <div className="grain-overlay" aria-hidden="true" />
            <AppRouter />
          </PlayerProvider>
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
